import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ColorPicker, Dialog, TextField, TRIP_HUES, vars, type TripColor } from "@pack-it-up/ui-web";
import { errorToMessage } from "@pack-it-up/viewmodel";
import { useSession } from "../../app/SessionProvider.js";

export const NewTripModal = ({ open, onClose }: { open: boolean; onClose: () => void }): ReactNode => {
  const { home } = useSession();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [location, setLocation] = useState("");
  const [color, setColor] = useState<TripColor>(TRIP_HUES[0]!);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = (): void => {
    setName("");
    setStart("");
    setEnd("");
    setLocation("");
    setColor(TRIP_HUES[0]!);
    setError(null);
    onClose();
  };

  const handleCreate = async (): Promise<void> => {
    if (name.trim() === "") return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await home.getState().createTrip({
        name: name.trim(),
        ...(start !== "" ? { startDate: start as `${string}-${string}-${string}` } : {}),
        ...(end !== "" ? { endDate: end as `${string}-${string}-${string}` } : {}),
        ...(location.trim() !== "" ? { location: location.trim() } : {}),
      });
      if (r.ok) {
        onClose();
        navigate(`/trip/${r.value}`);
        return;
      }
      setError(errorToMessage(r.error));
    } catch {
      // Belt-and-suspenders: a repo call should never throw (Result is the contract),
      // but this must never leave the button stuck on "Creating…" if one slips through.
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Plan a new trip"
      subtitle="You can fill in the rest later — just get it on the board."
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={() => void handleCreate()} disabled={name.trim() === "" || submitting}>
            {submitting ? "Creating…" : "Create trip"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: vars.space.md }}>
        {error !== null && (
          <div role="alert" style={{ fontSize: vars.font.sizeSm, color: vars.color.danger }}>
            {error}
          </div>
        )}
        <TextField
          label="Trip name"
          placeholder="e.g. Japan Spring '26"
          value={name}
          onChange={setName}
          autoFocus
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: vars.space.sm }}>
          <TextField label="Start date" type="date" value={start} onChange={setStart} />
          <TextField label="End date" type="date" value={end} onChange={setEnd} />
        </div>
        <TextField
          label="Location (optional)"
          placeholder="e.g. Tokyo, Japan"
          value={location}
          onChange={setLocation}
        />
        {/* Color swatches — visual only; color is derived from trip ID after creation */}
        <div>
          <div
            style={{
              fontSize: vars.font.sizeXs,
              color: vars.color.textMuted,
              marginBottom: vars.space.xs,
              fontWeight: vars.font.weightMedium,
            }}
          >
            Color
          </div>
          <ColorPicker value={color} onChange={setColor} label="Trip color" />
        </div>
      </div>
    </Dialog>
  );
};
